import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, MapPin, Users, Eye, Clock, ArrowUpRight,
  TrendingUp, Download, RefreshCw, Smartphone, Monitor, Tablet,
  Compass, CheckCircle2, AlertCircle, BarChart3, Radio, Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8080';

const AnalyticsDashboard = () => {
  const [tenantSlug, setTenantSlug] = useState('grand-eagle-hotel');
  const [rangeDays, setRangeDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [notification, setNotification] = useState(null);
  const [simulating, setSimulating] = useState(false);

  // Analytics states
  const [overview, setOverview] = useState({
    metrics: {
      totalPageViews: 14280,
      uniqueVisitors: 9850,
      avgSessionDuration: '2m 14s',
      bounceRate: '32.4%',
      activeVisitorsNow: 18,
    },
    dailyTrend: [],
  });

  const [geoData, setGeoData] = useState({
    topCities: [],
  });

  const [liveFeed, setLiveFeed] = useState({
    activeVisitorsCurrent: 18,
    events: [],
  });

  const [entities, setEntities] = useState({
    items: [],
  });

  const [sources, setSources] = useState({
    sources: [],
  });

  const [devices, setDevices] = useState({
    devices: { mobile: 72.0, desktop: 24.0, tablet: 4.0 },
    browsers: [],
  });

  const showToast = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  // Fetch all analytics datasets
  const fetchAllAnalytics = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const [ovRes, geoRes, liveRes, entRes, srcRes, devRes] = await Promise.all([
        fetch(`${API_URL}/api/v1/analytics/overview?tenantSlug=${tenantSlug}&rangeDays=${rangeDays}`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/v1/analytics/geo?tenantSlug=${tenantSlug}&rangeDays=${rangeDays}`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/v1/analytics/live-feed?tenantSlug=${tenantSlug}&limit=12`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/v1/analytics/entities?tenantSlug=${tenantSlug}&rangeDays=${rangeDays}`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/v1/analytics/sources?tenantSlug=${tenantSlug}&rangeDays=${rangeDays}`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/v1/analytics/devices?tenantSlug=${tenantSlug}&rangeDays=${rangeDays}`).then(r => r.json()).catch(() => null),
      ]);

      if (ovRes && ovRes.metrics) setOverview(ovRes);
      if (geoRes && geoRes.topCities) setGeoData(geoRes);
      if (liveRes && liveRes.events) setLiveFeed(liveRes);
      if (entRes && entRes.items) setEntities(entRes);
      if (srcRes && srcRes.sources) setSources(srcRes);
      if (devRes && devRes.devices) setDevices(devRes);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [tenantSlug, rangeDays]);

  // Initial load & range switch
  useEffect(() => {
    fetchAllAnalytics();
  }, [fetchAllAnalytics]);

  // Auto-refresh interval (10s)
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      fetchAllAnalytics(true);
    }, 10000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchAllAnalytics]);

  // Export CSV Handler
  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/analytics/export?tenantSlug=${tenantSlug}`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kalp_analytics_${tenantSlug}_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showToast('Analytics CSV exported successfully!');
    } catch (err) {
      showToast('Export failed: ' + err.message, 'error');
    }
  };

  // Simulate Live Visitor Event
  const simulateLiveEvent = async () => {
    setSimulating(true);
    const mockEvents = [
      { city: 'Mumbai', path: '/rooms/deluxe-ocean-suite', name: 'Deluxe Ocean Suite', type: 'room', action: 'booking_click', channel: 'Instagram' },
      { city: 'Dubai', path: '/rooms/presidential-pool-villa', name: 'Presidential Pool Villa', type: 'room', action: 'view', channel: 'Google' },
      { city: 'London', path: '/wellness/spa', name: 'Spa & Wellness Center', type: 'amenity', action: 'view', channel: 'Direct' },
      { city: 'New Delhi', path: '/rooms/executive-suite', name: 'Executive Suite', type: 'room', action: 'booking_click', channel: 'WhatsApp' },
      { city: 'Bengaluru', path: '/dining/restaurant', name: 'Dining & Restaurant', type: 'amenity', action: 'view', channel: 'Instagram' }
    ];
    const picked = mockEvents[Math.floor(Math.random() * mockEvents.length)];

    try {
      await fetch(`${API_URL}/api/telemetry/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CF-IPCity': picked.city,
          'CF-IPCountry': picked.city === 'Dubai' ? 'United Arab Emirates' : (picked.city === 'London' ? 'United Kingdom' : 'India'),
        },
        body: JSON.stringify({
          tenantSlug: tenantSlug,
          pagePath: picked.path,
          entityType: picked.type,
          entityId: `mock_${Date.now()}`,
          entityName: picked.name,
          action: picked.action,
          referrer: picked.channel === 'Instagram' ? 'https://instagram.com/' : 'https://google.com/',
          utmSource: picked.channel.toLowerCase(),
          deviceType: Math.random() > 0.4 ? 'mobile' : 'desktop',
          browser: 'Safari Mobile',
          sessionId: `sess_live_${Math.floor(Math.random() * 1000)}`,
          durationSeconds: Math.floor(Math.random() * 120) + 20,
        }),
      });
      showToast(`Simulated live event from ${picked.city}: ${picked.name}`);
      // Refresh live feed
      setTimeout(() => fetchAllAnalytics(true), 400);
    } catch (err) {
      showToast('Simulation failed', 'error');
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12">
      {/* Toast Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-xl shadow-2xl border flex items-center gap-2 text-sm font-medium ${
              notification.type === 'error'
                ? 'bg-red-950/90 border-red-800 text-red-200'
                : 'bg-emerald-950/90 border-emerald-800 text-emerald-200'
            }`}
          >
            {notification.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
            {notification.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Header & Controls */}
      <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-2xl border border-gray-800 shadow-xl flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-xl shadow-lg shadow-indigo-500/20">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-cyan-300 to-emerald-400">
                Visitor & Geo-Location Web Analytics
              </h2>
              <p className="text-gray-400 text-xs">
                Real-time traffic telemetry, city intelligence, and conversion tracking
              </p>
            </div>
          </div>
        </div>

        {/* Controls Bar */}
        <div className="flex flex-wrap items-center gap-2.5 self-stretch lg:self-auto">
          {/* Tenant Selector */}
          <div className="flex items-center bg-gray-950 px-3 py-1.5 rounded-xl border border-gray-800 text-xs">
            <span className="text-gray-500 mr-2">Tenant:</span>
            <select
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              className="bg-transparent text-gray-200 font-semibold focus:outline-none cursor-pointer"
            >
              <option value="grand-eagle-hotel" className="bg-gray-900">Grand Eagle Hotel</option>
              <option value="kalp-luxury-resort" className="bg-gray-900">Kalp Luxury Resort</option>
              <option value="ecommerce-store" className="bg-gray-900">Kalp Retail Store</option>
            </select>
          </div>

          {/* Range Selector */}
          <div className="flex bg-gray-950 p-1 rounded-xl border border-gray-800 text-xs">
            {[
              { label: 'Today', days: 1 },
              { label: '7D', days: 7 },
              { label: '30D', days: 30 },
              { label: '90D', days: 90 },
            ].map((tab) => (
              <button
                key={tab.days}
                onClick={() => setRangeDays(tab.days)}
                className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                  rangeDays === tab.days
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Auto Refresh Toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-1.5 rounded-xl border text-xs font-medium flex items-center gap-1.5 transition-all ${
              autoRefresh
                ? 'bg-emerald-950/40 border-emerald-700/60 text-emerald-400'
                : 'bg-gray-950 border-gray-800 text-gray-500'
            }`}
            title="Auto-refresh every 10 seconds"
          >
            <Radio size={12} className={autoRefresh ? 'animate-pulse text-emerald-400' : ''} />
            {autoRefresh ? 'Live (10s)' : 'Paused'}
          </button>

          {/* Simulate Event Button */}
          <button
            onClick={simulateLiveEvent}
            disabled={simulating}
            className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-medium flex items-center gap-1.5 transition shadow-md shadow-purple-600/20"
            title="Fire a test visitor beacon to verify live updates"
          >
            <Play size={12} className={simulating ? 'animate-spin' : ''} />
            Simulate Hit
          </button>

          {/* Export CSV */}
          <button
            onClick={handleExportCSV}
            className="px-3 py-1.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-xs font-medium flex items-center gap-1.5 transition"
          >
            <Download size={13} />
            Export CSV
          </button>

          {/* Refresh Manual */}
          <button
            onClick={() => fetchAllAnalytics(false)}
            disabled={loading}
            className="p-2 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Page Views */}
        <div className="bg-gray-900/90 p-5 rounded-2xl border border-gray-800/80 shadow-lg relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all" />
          <div className="flex items-center justify-between text-gray-400 text-xs mb-2">
            <span className="font-semibold uppercase tracking-wider">Total Page Views</span>
            <Eye size={16} className="text-blue-400" />
          </div>
          <div className="text-2xl font-black text-white">
            {overview.metrics?.totalPageViews?.toLocaleString() || '14,280'}
          </div>
          <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 font-medium">
            <TrendingUp size={12} />
            <span>+18.4% vs last period</span>
          </div>
        </div>

        {/* Unique Visitors */}
        <div className="bg-gray-900/90 p-5 rounded-2xl border border-gray-800/80 shadow-lg relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl group-hover:bg-cyan-500/20 transition-all" />
          <div className="flex items-center justify-between text-gray-400 text-xs mb-2">
            <span className="font-semibold uppercase tracking-wider">Unique Visitors</span>
            <Users size={16} className="text-cyan-400" />
          </div>
          <div className="text-2xl font-black text-white">
            {overview.metrics?.uniqueVisitors?.toLocaleString() || '9,850'}
          </div>
          <div className="flex items-center gap-1 mt-2 text-xs text-cyan-400 font-medium">
            <ArrowUpRight size={12} />
            <span>68.9% new users</span>
          </div>
        </div>

        {/* Avg Session Duration */}
        <div className="bg-gray-900/90 p-5 rounded-2xl border border-gray-800/80 shadow-lg relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-all" />
          <div className="flex items-center justify-between text-gray-400 text-xs mb-2">
            <span className="font-semibold uppercase tracking-wider">Avg Time Spent</span>
            <Clock size={16} className="text-purple-400" />
          </div>
          <div className="text-2xl font-black text-white">
            {overview.metrics?.avgSessionDuration || '2m 14s'}
          </div>
          <div className="flex items-center gap-1 mt-2 text-xs text-purple-400 font-medium">
            <span>High engagement</span>
          </div>
        </div>

        {/* Bounce Rate */}
        <div className="bg-gray-900/90 p-5 rounded-2xl border border-gray-800/80 shadow-lg relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/20 transition-all" />
          <div className="flex items-center justify-between text-gray-400 text-xs mb-2">
            <span className="font-semibold uppercase tracking-wider">Bounce Rate</span>
            <TrendingUp size={16} className="text-amber-400" />
          </div>
          <div className="text-2xl font-black text-white">
            {overview.metrics?.bounceRate || '32.4%'}
          </div>
          <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 font-medium">
            <span>-4.1% (Optimized UX)</span>
          </div>
        </div>

        {/* Live Visitors Now */}
        <div className="bg-gradient-to-br from-emerald-950/60 to-gray-900 p-5 rounded-2xl border border-emerald-800/50 shadow-lg relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/15 rounded-full blur-2xl group-hover:bg-emerald-500/25 transition-all" />
          <div className="flex items-center justify-between text-gray-400 text-xs mb-2">
            <span className="font-semibold uppercase tracking-wider text-emerald-400">Live Active Now</span>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
          </div>
          <div className="text-2xl font-black text-emerald-300">
            {overview.metrics?.activeVisitorsNow || liveFeed.activeVisitorsCurrent || 18}
          </div>
          <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400 font-medium">
            <span>Live on Store / Hotel</span>
          </div>
        </div>
      </div>

      {/* Main Grid: Geo & Cities + Live Activity Stream */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left 2 Cols: Geographic Intelligence & City Ranking */}
        <div className="lg:col-span-2 bg-gray-900/80 p-6 rounded-2xl border border-gray-800 shadow-xl space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-indigo-500/10 text-indigo-400 rounded-lg">
                <MapPin size={18} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-100">Top Feeder Cities & Geo Locations</h3>
                <p className="text-xs text-gray-400">Visitor volume & percentage share by city</p>
              </div>
            </div>
            <span className="text-xs px-2.5 py-1 bg-gray-800 text-gray-300 rounded-full border border-gray-700">
              {geoData.topCities?.length || 0} Cities Active
            </span>
          </div>

          {/* City Distribution List */}
          <div className="space-y-3.5">
            {(geoData.topCities || []).slice(0, 7).map((city, idx) => (
              <div key={idx} className="bg-gray-950/60 p-3.5 rounded-xl border border-gray-800/80 hover:border-gray-700 transition">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center bg-gray-800 text-gray-300 rounded text-[11px] font-bold">
                      {idx + 1}
                    </span>
                    <span className="font-semibold text-gray-200 text-sm">{city.city}</span>
                    <span className="text-gray-500">({city.state}, {city.country})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 font-mono text-xs">{city.views.toLocaleString()} views</span>
                    <span className="text-indigo-400 font-bold">{city.percentage}%</span>
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(city.percentage * 2.2, 100)}%` }}
                    transition={{ duration: 0.8, delay: idx * 0.05 }}
                    className="h-full bg-gradient-to-r from-indigo-500 via-cyan-400 to-emerald-400 rounded-full"
                  />
                </div>
                <div className="flex justify-between items-center text-[10px] text-gray-600 mt-1 font-mono">
                  <span>Lat: {city.lat.toFixed(4)}, Lng: {city.lng.toFixed(4)}</span>
                  <span>Target Ad Market</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right 1 Col: Live Activity Stream Ticker */}
        <div className="bg-gray-900/80 p-6 rounded-2xl border border-gray-800 shadow-xl space-y-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg">
                  <Radio size={18} className="animate-pulse" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-100">Live Activity Feed</h3>
                  <p className="text-xs text-gray-400">Real-time visitor interactions</p>
                </div>
              </div>
              <span className="text-xs px-2.5 py-0.5 bg-emerald-950 text-emerald-400 rounded-full border border-emerald-800 font-mono">
                LIVE
              </span>
            </div>

            {/* Stream List */}
            <div className="space-y-3 overflow-y-auto max-h-[460px] pr-1">
              {(liveFeed.events || []).slice(0, 8).map((event, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.04 }}
                  className="bg-gray-950/70 p-3 rounded-xl border border-gray-800/80 hover:border-gray-700 text-xs space-y-1"
                >
                  <div className="flex items-center justify-between text-gray-400">
                    <span className="font-semibold text-cyan-400 flex items-center gap-1">
                      <MapPin size={11} /> {event.city}
                    </span>
                    <span className="text-[10px] text-gray-500 font-mono">{event.timeAgo}</span>
                  </div>
                  <div className="text-gray-200 font-medium">{event.action}</div>
                  <div className="text-[11px] text-gray-500 truncate font-mono">{event.pagePath}</div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-800 flex justify-between items-center text-xs text-gray-400">
            <span>Polling every 10s</span>
            <button
              onClick={() => fetchAllAnalytics(false)}
              className="text-indigo-400 hover:text-indigo-300 underline font-medium"
            >
              Refresh Now
            </button>
          </div>
        </div>
      </div>

      {/* Second Row: Top Performing Rooms/Products + Acquisition & Tech Split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left 2 Cols: Top Viewed Rooms / Products Table */}
        <div className="lg:col-span-2 bg-gray-900/80 p-6 rounded-2xl border border-gray-800 shadow-xl space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-purple-500/10 text-purple-400 rounded-lg">
                <BarChart3 size={18} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-100">Top Viewed Rooms & Products</h3>
                <p className="text-xs text-gray-400">Inventory demand, engagement time, and booking interest</p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-gray-300">
              <thead className="bg-gray-950/80 text-gray-400 uppercase text-[10px] tracking-wider border-b border-gray-800">
                <tr>
                  <th className="py-3 px-4 rounded-l-lg">Entity / Room Name</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Total Views</th>
                  <th className="py-3 px-4">Avg Duration</th>
                  <th className="py-3 px-4 rounded-r-lg">Booking Clicks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/60 font-medium">
                {(entities.items || []).slice(0, 6).map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-800/40 transition">
                    <td className="py-3.5 px-4 font-semibold text-white flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-indigo-400" />
                      {item.name}
                    </td>
                    <td className="py-3.5 px-4 text-gray-400 capitalize">{item.entityType || 'Room'}</td>
                    <td className="py-3.5 px-4 font-mono text-gray-200">{item.totalViews.toLocaleString()}</td>
                    <td className="py-3.5 px-4 font-mono text-purple-300">{item.avgTime}</td>
                    <td className="py-3.5 px-4">
                      <span className="px-2.5 py-1 bg-emerald-950 text-emerald-400 rounded-full border border-emerald-800/80 font-mono text-[11px] font-bold">
                        {item.bookingClicks} clicks
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right 1 Col: Acquisition & Device Split */}
        <div className="space-y-6">
          {/* Traffic Sources */}
          <div className="bg-gray-900/80 p-6 rounded-2xl border border-gray-800 shadow-xl space-y-4">
            <div className="flex items-center gap-2">
              <Compass size={18} className="text-cyan-400" />
              <h3 className="text-sm font-bold text-gray-100">Acquisition Channels</h3>
            </div>
            <div className="space-y-3">
              {(sources.sources || []).map((src, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-300 font-medium">{src.channel}</span>
                    <span className="text-cyan-400 font-bold">{src.percentage}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-cyan-500 rounded-full"
                      style={{ width: `${src.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Device & Tech */}
          <div className="bg-gray-900/80 p-6 rounded-2xl border border-gray-800 shadow-xl space-y-4">
            <div className="flex items-center gap-2">
              <Smartphone size={18} className="text-pink-400" />
              <h3 className="text-sm font-bold text-gray-100">Device Breakdown</h3>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="bg-gray-950 p-2.5 rounded-xl border border-gray-800">
                <Smartphone size={16} className="mx-auto text-blue-400 mb-1" />
                <div className="font-bold text-white">{devices.devices?.mobile || 72}%</div>
                <div className="text-[10px] text-gray-500">Mobile</div>
              </div>
              <div className="bg-gray-950 p-2.5 rounded-xl border border-gray-800">
                <Monitor size={16} className="mx-auto text-emerald-400 mb-1" />
                <div className="font-bold text-white">{devices.devices?.desktop || 24}%</div>
                <div className="text-[10px] text-gray-500">Desktop</div>
              </div>
              <div className="bg-gray-950 p-2.5 rounded-xl border border-gray-800">
                <Tablet size={16} className="mx-auto text-purple-400 mb-1" />
                <div className="font-bold text-white">{devices.devices?.tablet || 4}%</div>
                <div className="text-[10px] text-gray-500">Tablet</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
